import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  Bell,
  Check,
  Clock,
  Copy,
  ExternalLink,
  MessageSquare,
  MoreVertical,
  PlusSquare,
  Share2,
  Smartphone,
  X,
} from "lucide-react";
import "./MobileAppPromptModal.css";

const PWA_URL =
  process.env.REACT_APP_PWA_URL || "https://students.wccovolunteers.co.za";

export default function MobileAppPromptModal({ show, onHide }) {
  const [activeTab, setActiveTab] = useState("ios");
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(PWA_URL).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    }
  };

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
    PWA_URL
  )}`;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="mobile-app-modal"
      backdrop="static"
      keyboard={true}
    >
      <div className="mobile-app-modal__header">
        <button
          type="button"
          className="mobile-app-modal__close"
          onClick={onHide}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="mobile-app-modal__eyebrow">WCCO Volunteer Mobile Application</div>
        <h3 className="mobile-app-modal__title">Install the WCCO Volunteer mobile application</h3>
        <p className="mobile-app-modal__subtitle">
          Access project chat, offline volunteer hour logging, and instant push notifications directly on your mobile device.
        </p>
      </div>

      <div className="mobile-app-modal__body">
        {/* Three core feature cards */}
        <div className="mobile-app-features">
          <div className="mobile-app-feature">
            <div className="mobile-app-feature__icon">
              <MessageSquare size={18} />
            </div>
            <strong>Project chat</strong>
            <span>Direct and group conversations with project leaders and team members.</span>
          </div>

          <div className="mobile-app-feature">
            <div className="mobile-app-feature__icon">
              <Clock size={18} />
            </div>
            <strong>Offline hour logs</strong>
            <span>Log hours and attach photo evidence even without an active internet connection.</span>
          </div>

          <div className="mobile-app-feature">
            <div className="mobile-app-feature__icon">
              <Bell size={18} />
            </div>
            <strong>Push notifications</strong>
            <span>Receive immediate alerts when volunteer hours are approved or projects update.</span>
          </div>
        </div>

        {/* Quick Launch & QR Code Panel */}
        <div className="mobile-app-access">
          <div className="mobile-app-qr">
            <img src={qrCodeUrl} alt="QR code to access the mobile application" loading="lazy" />
            <small>Scan with phone</small>
          </div>
          <div className="mobile-app-access-copy">
            <h4>Access the mobile application</h4>
            <p>
              Open on your mobile phone browser to install directly to your home screen with zero app store downloads required.
            </p>
            <div className="mobile-app-access-buttons">
              <a
                href={PWA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-app-btn-open"
              >
                <Smartphone size={16} />
                <span>Open mobile app</span>
                <ExternalLink size={14} />
              </a>

              <button
                type="button"
                className="mobile-app-btn-copy"
                onClick={handleCopyLink}
              >
                {copied ? (
                  <>
                    <Check size={16} />
                    <span>Link copied</span>
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    <span>Copy link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Installation Instructions */}
        <div>
          <h4 className="mobile-app-section-title">
            Installation instructions
          </h4>

          <div className="mobile-app-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "ios"}
              className={`mobile-app-tab-btn${activeTab === "ios" ? " active" : ""}`}
              onClick={() => setActiveTab("ios")}
            >
              <span>Apple iPhone & iPad (Safari)</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "android"}
              className={`mobile-app-tab-btn${activeTab === "android" ? " active" : ""}`}
              onClick={() => setActiveTab("android")}
            >
              <span>Android (Google Chrome)</span>
            </button>
          </div>

          {activeTab === "ios" ? (
            <ul className="mobile-app-steps">
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">1</span>
                <span className="mobile-app-step__text">
                  Open <strong>Safari</strong> on your iPhone and visit: <code>{PWA_URL}</code>
                </span>
              </li>
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">2</span>
                <span className="mobile-app-step__text">
                  Tap the <span className="mobile-app-step__tag"><Share2 size={13} /> Share</span> button in the bottom toolbar of Safari.
                </span>
              </li>
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">3</span>
                <span className="mobile-app-step__text">
                  Scroll down and tap <span className="mobile-app-step__tag"><PlusSquare size={13} /> Add to Home Screen</span>.
                </span>
              </li>
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">4</span>
                <span className="mobile-app-step__text">
                  Tap <strong>Add</strong> in the top-right corner to complete installation.
                </span>
              </li>
            </ul>
          ) : (
            <ul className="mobile-app-steps">
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">1</span>
                <span className="mobile-app-step__text">
                  Open <strong>Chrome</strong> on your Android device and visit: <code>{PWA_URL}</code>
                </span>
              </li>
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">2</span>
                <span className="mobile-app-step__text">
                  Tap the browser menu <span className="mobile-app-step__tag"><MoreVertical size={13} /> three dots</span> in the top-right corner.
                </span>
              </li>
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">3</span>
                <span className="mobile-app-step__text">
                  Tap <span className="mobile-app-step__tag">Install app</span> or <span className="mobile-app-step__tag">Add to Home screen</span>.
                </span>
              </li>
              <li className="mobile-app-step">
                <span className="mobile-app-step__num">4</span>
                <span className="mobile-app-step__text">
                  Tap <strong>Install</strong> to confirm. The application will launch in full screen.
                </span>
              </li>
            </ul>
          )}
        </div>
      </div>

      <div className="mobile-app-modal__footer">
        <small>Sign in using your existing student credentials.</small>
        <button
          type="button"
          className="mobile-app-dismiss-btn"
          onClick={onHide}
        >
          Continue to web portal
        </button>
      </div>
    </Modal>
  );
}
